import axios from 'axios';

export type AppErrorKind =
  | 'network'
  | 'timeout'
  | 'canceled'
  | 'auth'
  | 'forbidden'
  | 'notFound'
  | 'validation'
  | 'server'
  | 'unknown';

/**
 * The single error shape the application sees; nothing outside this module handles a raw
 * AxiosError. A real Error subclass, not a plain object: a non-Error throw loses the stack
 * trace, breaks `instanceof Error` in third-party code, and degrades what Sentry reports.
 */
export class AppError extends Error {
  readonly kind: AppErrorKind;
  readonly status?: number;
  /** Backend error code when the payload carries one; `client.`-prefixed when this client set it. */
  readonly code?: string;
  /**
   * The Problem payload's `detail` alone, when the server sent one. `message` falls back through
   * `title` and the transport's own wording, so it cannot tell a caller whether the server spoke;
   * a caller that wants to render the server's sentence and otherwise word the failure itself
   * reads this instead.
   */
  readonly detail?: string;
  /** Field-level messages, fed straight into react-hook-form's setError. */
  readonly fieldErrors?: Record<string, string[]>;
  readonly traceId?: string;

  constructor(init: {
    kind: AppErrorKind;
    message: string;
    status?: number;
    code?: string;
    detail?: string;
    fieldErrors?: Record<string, string[]>;
    traceId?: string;
    /** Original error, preserved for Sentry. */
    cause?: unknown;
  }) {
    super(init.message, { cause: init.cause });
    this.name = 'AppError';
    this.kind = init.kind;
    this.status = init.status;
    this.code = init.code;
    this.detail = init.detail;
    this.fieldErrors = init.fieldErrors;
    this.traceId = init.traceId;
  }
}

/** RFC 7807-ish payload; every field is optional since the API's shape varies by endpoint. */
type ProblemPayload = {
  title?: string;
  detail?: string;
  code?: string;
  traceId?: string;
  errors?: Record<string, string[]>;
  message?: string;
};

function kindForStatus(status: number): AppErrorKind {
  if (status === 401) return 'auth';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'notFound';
  if (status === 422 || status === 400) return 'validation';
  if (status >= 500) return 'server';
  return 'unknown';
}

export function isAppError(value: unknown): value is AppError {
  return value instanceof AppError;
}

/**
 * Normalizes anything thrown by the HTTP layer into an AppError. Pure and free of
 * framework imports so it stays unit-testable and is the single seam to change if the
 * API's error shape changes.
 */
export function toAppError(error: unknown): AppError {
  if (isAppError(error)) return error;

  if (axios.isCancel(error)) {
    return new AppError({ kind: 'canceled', message: 'Request canceled', cause: error });
  }

  if (axios.isAxiosError(error)) {
    if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
      return new AppError({ kind: 'timeout', message: 'The request timed out', cause: error });
    }

    // No response object means the request never completed — DNS failure,
    // offline, CORS rejection. Distinct from a 5xx, which did reach the server.
    if (!error.response) {
      return new AppError({ kind: 'network', message: 'Unable to reach the server', cause: error });
    }

    const status = error.response.status;
    const payload = (error.response.data ?? {}) as ProblemPayload;

    return new AppError({
      kind: kindForStatus(status),
      status,
      code: payload.code,
      detail: payload.detail,
      message: payload.detail ?? payload.title ?? payload.message ?? error.message,
      fieldErrors: payload.errors,
      traceId: payload.traceId,
      cause: error,
    });
  }

  return new AppError({
    kind: 'unknown',
    message: error instanceof Error ? error.message : 'Something went wrong',
    cause: error,
  });
}
